import { createFileRoute, useBlocker } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { PhoneApp } from '@/components/phone'
import Header from '@/components/Header'
import { useVoiceServiceContext, VoiceServiceProvider } from '@/contexts/VoiceServiceContext'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export const Route = createFileRoute('/dialer')({
  staticData: {
    title: 'Dialer — PhoneSalad',
    description: 'Start making international calls right from your browser. Dial any number worldwide at low per-minute rates with no downloads or app required.',
    sitemap: { priority: '0.5', changefreq: 'monthly' },
  },
  component: DialerRoute,
})

function DialerRoute() {
  return (
    <VoiceServiceProvider>
      <DialerPage />
    </VoiceServiceProvider>
  )
}

const ACTIVE_CALL_STATUSES = new Set(['connecting', 'ringing', 'in-progress', 'reconnecting', 'validating'])

function NavigationGuard() {
  const { callState } = useVoiceServiceContext()
  const { t } = useTranslation('app')
  const isCallActive = ACTIVE_CALL_STATUSES.has(callState.status)

  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: () => isCallActive,
    enableBeforeUnload: () => isCallActive,
    withResolver: true,
  })

  return (
    <AlertDialog open={status === 'blocked'} onOpenChange={(open) => { if (!open) reset?.() }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('dialer.leaveWarningTitle', 'Leave this page?')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('dialer.leaveWarningDescription', 'You have an active call. Leaving this page will end the call.')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => reset?.()}>
            {t('dialer.stayOnPage', 'Stay on page')}
          </AlertDialogCancel>
          <AlertDialogAction onClick={() => proceed?.()} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            {t('dialer.leavePage', 'Leave & end call')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function DialerPage() {
  const { requestInit } = useVoiceServiceContext()

  // Request voice service initialization on first visit to dialer
  useEffect(() => {
    requestInit()
  }, [requestInit])

  return (
    <div className="h-dvh flex flex-col bg-background">
      {/* Desktop Header */}
      <div className="hidden lg:block">
        <Header />
      </div>

      <main className="flex-1 lg:p-0 flex flex-col min-h-0">
        <NavigationGuard />
        <PhoneApp />
      </main>
    </div>
  )
}
